import React from 'react';
import { Box, Typography } from '@mui/material';
import { AuditChange } from './auditApi';
import { hiddenCount, isMasked, valueToText } from './auditFormat';

/**
 * Общие части карточки записи: журнал действий (AuditDetailsDrawer) и «История» на экранах отчётности
 * (reports/history) показывают изменения одинаково.
 */
export const BORDER = '1px solid #e2e8f0';
export const LABEL_SX = { fontSize: '0.8rem', color: '#64748b' } as const;
export const VALUE_SX = { fontSize: '0.85rem', color: '#0f172a', wordBreak: 'break-word' } as const;

export const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4, mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

/** Строка «название - значение»; пустые значения не выводятся */
export const Field: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', gap: 2, py: 0.6, borderBottom: '1px solid #f1f5f9' }}>
      <Typography sx={{ ...LABEL_SX, width: 150, flexShrink: 0 }}>{label}</Typography>
      <Typography sx={VALUE_SX}>{value}</Typography>
    </Box>
  );
};

export const Pairs: React.FC<{ pairs: Array<{ key: string; value: string }> }> = ({ pairs }) => (
  <Box>
    {pairs.map((pair, index) => (
      <Box key={`${pair.key}-${index}`} sx={{ display: 'flex', gap: 2, py: 0.6, borderBottom: '1px solid #f1f5f9' }}>
        {pair.key !== '' && <Typography sx={{ ...LABEL_SX, width: 150, flexShrink: 0 }}>{pair.key}</Typography>}
        <Typography sx={{ ...VALUE_SX, fontFamily: isMasked(pair.value) ? 'monospace' : undefined }}>{pair.value}</Typography>
      </Box>
    ))}
  </Box>
);

/** Изменения «было → стало»: значение «было» зачеркнуто и приглушено, «стало» выделено */
export const ChangesList: React.FC<{ changes: AuditChange[]; total: number | undefined }> = ({ changes, total }) => {
  const hidden = hiddenCount(changes.length, total);
  return (
    <Box sx={{ border: BORDER, borderRadius: '8px', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: 1.5, px: 1.5, py: 0.75, backgroundColor: '#f8fafc', borderBottom: BORDER }}>
        <Typography sx={{ ...LABEL_SX, flex: '1 1 40%', fontWeight: 700 }}>Поле</Typography>
        <Typography sx={{ ...LABEL_SX, flex: '1 1 30%', fontWeight: 700 }}>Было</Typography>
        <Typography sx={{ ...LABEL_SX, flex: '1 1 30%', fontWeight: 700 }}>Стало</Typography>
      </Box>
      {changes.map((change, index) => (
        <Box
          key={`${change.field}-${index}`}
          sx={{ display: 'flex', gap: 1.5, px: 1.5, py: 0.9, borderBottom: index < changes.length - 1 ? '1px solid #f1f5f9' : 'none' }}
        >
          <Typography sx={{ ...VALUE_SX, flex: '1 1 40%' }}>{change.label || change.field}</Typography>
          <Typography sx={{ ...VALUE_SX, flex: '1 1 30%', color: '#94a3b8', textDecoration: 'line-through' }}>{valueToText(change.from)}</Typography>
          <Typography sx={{ ...VALUE_SX, flex: '1 1 30%', fontWeight: 700 }}>{valueToText(change.to)}</Typography>
        </Box>
      ))}
      {hidden > 0 && (
        <Typography sx={{ ...LABEL_SX, px: 1.5, py: 0.9, borderTop: BORDER }}>
          Показаны первые {changes.length} из {total}
        </Typography>
      )}
    </Box>
  );
};
